import { Box, Typography } from '@mui/material';
import { useListContext, useTranslate } from 'react-admin';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';
import { styled } from '@mui/material/styles';
import React from 'react';
import PropTypes from 'prop-types';
import { NoDataPlaceholder } from '@/components/analytics/common/NoDataPlaceholder.jsx';
import { formatTimeInterval } from '@/helpers/date/formatTimeInterval.js';
import { DrawerLink } from '@/components/global/drawers/DrawerLink.jsx';
import { Link } from 'react-router-dom';
import { analyticsStyles } from '@/themes/commonStyles/analytics.js';
import { Palette } from '@/themes/themeOverrides/Palette';

const StyledDurationStat = styled(Box)(() => ({
    borderTop: `2px solid ${Palette.gray.gray75}`,
    display: 'flex',
    alignItems: 'center',
    padding: '9px 0',
    '& .MuiTypography-caption': {
        fontSize: '14px',
    },
    '& .MuiTypography-root.MuiTypography-h5': {
        fontSize: '14px',
        fontWeight: '600',
    },
}));

const StyledDeviceWorkAnalytics = styled(Box)(() => ({
    backgroundColor: Palette.white,
    padding: '40px 40px 48px',
    borderRadius: '16px',
    [`@media (max-width: ${breakpoints.values.md}px)`]: {
        padding: '24px',
    },
    '& .ranking-header': {
        ...analyticsStyles.rankingHeader,
    },
    '& .device-work-analytics-content': {
        display: 'flex',
        alignItems: 'flex-start',
        alignSelf: 'stretch',
        gap: '32px',
        paddingTop: '4px',
        [`@media (max-width: ${breakpoints.values.sm}px)`]: {
            flexDirection: 'column',
            gap: '40px',
        },
        '& .column': {
            display: 'flex',
            flexDirection: 'column',
            flex: '1 0 0',
            [`@media (max-width: ${breakpoints.values.sm}px)`]: {
                width: '100%',
            },
            '& .MuiTypography-body2': {
                fontSize: '14px',
                fontWeight: '600',
                color: Palette.black,
                lineHeight: '20px',
                marginBottom: '10px',
            },
            '& > .MuiBox-root': {
                padding: '20px 0',
                [`@media (max-width: ${breakpoints.values.sm}px)`]: {
                    padding: '10px 0',
                },
                '&:last-child': {
                    paddingBottom: '0',
                },
                '& .MuiTypography-root': {
                    lineHeight: '20px',
                    display: 'block',
                    '& + .MuiTypography-root': {
                        marginTop: '2px',
                    },
                    '& + .MuiBox-root': {
                        height: '21px',
                    },
                },
            },
        },
    },
}));

const getItemValue = (resource, item) => {
    if (!item) {
        return null;
    }

    if ('bag' === resource) {
        return item.fillingTime;
    }

    return item.duration;
};

const DurationResourceLink = ({ resource, item, children }) => {
    if (!item) {
        return children;
    }

    if ('bag' === resource) {
        const filter = { search: { value: item.seal } };
        const link = `/bag?filter=${JSON.stringify(filter)}`;

        return (
            <Link style={{ color: Palette.black, textDecoration: 'none' }} to={link}>
                {children}
            </Link>
        );
    }

    return <DrawerLink resource={resource} source={item.id} action='show' relative label={children} />;
};

const DurationStat = ({ label, value, resource, item }) => {
    const itemValue = getItemValue(resource, item) ?? value;

    return (
        <StyledDurationStat>
            <Box>
                <Typography variant='caption'>{label}</Typography>
                {null !== itemValue && !isNaN(itemValue) ? (
                    <DurationResourceLink resource={resource} item={item}>
                        <Typography variant='h5'>{formatTimeInterval(itemValue)}</Typography>
                    </DurationResourceLink>
                ) : (
                    <NoDataPlaceholder />
                )}
            </Box>
        </StyledDurationStat>
    );
};

export const TransactionDurationAnalytics = () => {
    const { data } = useListContext();
    const translate = useTranslate();
    if (!data || data.length === 0) return null;

    const analytic = data.find(s => s.id === 'current').data;
    const duration = data.find(s => s.id === 'duration').data;

    return (
        <StyledDeviceWorkAnalytics>
            <Box className='ranking-header'>
                <Typography variant='h6'>{translate('resources.collectionPoint.labels.deviceWork')}</Typography>
            </Box>
            <Box className='device-work-analytics-content'>
                <Box className='column'>
                    <Typography variant='body2'>{translate('resources.transaction.labels.transactionTimeCaption')}</Typography>
                    <DurationStat
                        label={translate('analytics.transaction.averageDuration')}
                        value={analytic.transactionsPeriodSum / analytic.transactionsCount}
                    />
                    <DurationStat label={translate('analytics.transaction.longestDuration')} resource='transaction' item={duration.transaction.longest} />
                    <DurationStat label={translate('analytics.transaction.shortestDuration')} resource='transaction' item={duration.transaction.shortest} />
                </Box>
                <Box className='column'>
                    <Typography variant='body2'>{translate('resources.transaction.labels.containerFillingTime')}</Typography>
                    <DurationStat label={translate('analytics.container.avgFillingTime')} value={analytic.bagsFillingTimeSum / analytic.bagsCount} />
                    <DurationStat label={translate('analytics.container.longestFilling')} resource='bag' item={duration.bag.longest} />
                    <DurationStat label={translate('analytics.container.shortestFilling')} resource='bag' item={duration.bag.shortest} />
                </Box>
            </Box>
        </StyledDeviceWorkAnalytics>
    );
};

DurationResourceLink.propTypes = {
    resource: PropTypes.oneOf(['transaction', 'bag']),
    item: PropTypes.object,
    children: PropTypes.node.isRequired,
};

DurationStat.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.number,
    resource: PropTypes.oneOf(['transaction', 'bag']),
    item: PropTypes.object,
};
